<template>
  <v-app id="terms-conditions">
    <!-- Header -->
    <div class="terms-header" :style="headerStyle">
      <div class="container">
        <div class="terms-header__content">
          <v-btn icon small class="terms-header__back" @click="goBack">
            <v-icon size="20" color="white">mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <h1 class="terms-header__title">Conditions Générales d'Utilisation</h1>
            <p class="terms-header__date">Date d'entrée en vigueur : 11/11/2025</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="container terms-content" :style="contentStyle">
      <div class="terms-card">
        <!-- Section 1 -->
        <section class="terms-section">
          <h2 class="section-title">1. Objet et Champ d'Application</h2>
          <p class="section-text">
            Les présentes Conditions Générales d'Utilisation (ci-après les CGU) régissent l'accès et l'utilisation de l'application mobile et du site web UrbanGO (ci-après la Plateforme), édités par la société UrbanGO.
          </p>
          <p class="section-text">
            UrbanGO est une société de droit ivoirien, représentée par son Gérant, Mohamed FADIGA, et dont le siège social est situé à Yopougon quartier Maroc, station Pétro Ivoire.
          </p>
          <p class="section-text">
            La Plateforme a pour objet de mettre en relation des compagnies de transport (ci-après les Transporteurs) avec des clients (ci-après l'Utilisateur ou le Passager) souhaitant acheter des billets de transport en ligne.
          </p>
          <p class="section-text">
            L'accès et l'utilisation de la Plateforme impliquent l'acceptation sans réserve des présentes CGU par l'Utilisateur.
          </p>
        </section>

        <!-- Section 2 -->
        <section class="terms-section">
          <h2 class="section-title">2. Rôle de UrbanGO : Intermédiaire de Vente</h2>
          <p class="section-text">
            UrbanGO agit exclusivement en qualité d'intermédiaire de vente pour le compte des Transporteurs.
          </p>
          <p class="section-text"><strong>Conséquences de la qualité d'intermédiaire :</strong></p>
          <ul class="section-list">
            <li>UrbanGO est responsable de la bonne exécution des services de mise en relation et de vente de billets en ligne.</li>
            <li>UrbanGO n'est pas responsable de l'exécution du service de transport lui-même (retards, annulations, accidents, perte de bagages, qualité du service, etc.). La responsabilité de ces éléments incombe exclusivement au Transporteur.</li>
          </ul>
        </section>

        <!-- Section 3 -->
        <section class="terms-section">
          <h2 class="section-title">3. Obligations d'Information</h2>
          <p class="section-text">
            Conformément à la Loi n° 2013-546 relative aux transactions électroniques et à la Loi n° 2016-412 relative à la consommation, UrbanGO s'engage à fournir les informations suivantes :
          </p>

          <div class="terms-table">
            <div class="table-row table-header">
              <div class="table-cell">Informations</div>
              <div class="table-cell">Obligations</div>
            </div>
            <div class="table-row">
              <div class="table-cell cell-bold">Identification</div>
              <div class="table-cell">Nom, adresse, numéro de téléphone, adresse e-mail du Gérant et de la société UrbanGO</div>
            </div>
            <div class="table-row">
              <div class="table-cell cell-bold">Caractéristiques essentielles</div>
              <div class="table-cell">Description claire du service de vente de billets et du rôle d'intermédiaire.</div>
            </div>
            <div class="table-row">
              <div class="table-cell cell-bold">Prix</div>
              <div class="table-cell">Prix total du billet, comprenant le prix du transport fixé par le transporteur et les frais de réservation facturés par UrbanGO.</div>
            </div>
            <div class="table-row">
              <div class="table-cell cell-bold">Modalités de paiement</div>
              <div class="table-cell">Acceptation des paiements via Wave et autres méthodes spécifiées.</div>
            </div>
            <div class="table-row">
              <div class="table-cell cell-bold">Processus de commande</div>
              <div class="table-cell">Étapes claires pour la conclusion du contrat de vente de billet en ligne.</div>
            </div>
          </div>
        </section>

        <!-- Section 4 -->
        <section class="terms-section">
          <h2 class="section-title">4. Processus de Réservation et Paiement</h2>

          <h3 class="subsection-title">4.1. Conclusion du Contrat</h3>
          <p class="section-text">
            La réservation est effectuée par voie électronique. Le contrat de vente du billet est réputé conclu dès que l'Utilisateur a validé sa commande et que le paiement a été confirmé par le système de paiement.
          </p>

          <h3 class="subsection-title">4.2. Prix et Frais de Réservation</h3>
          <p class="section-text">Le prix affiché comprend :</p>
          <ol class="section-list">
            <li>Le coût du transport fixé par le Transporteur.</li>
            <li>Les frais de réservation (commission) prélevés par UrbanGO pour son service d'intermédiation.</li>
          </ol>

          <h3 class="subsection-title">4.3. Paiement via Wave</h3>
          <p class="section-text">
            Le paiement est effectué via la solution Wave. L'Utilisateur accepte que le processus de paiement soit géré de manière automatisée pour permettre :
          </p>
          <ul class="section-list">
            <li>Le versement du montant net du transport au compte Wave du Transporteur.</li>
            <li>Le prélèvement automatique des frais de réservation sur le compte d'UrbanGO.</li>
          </ul>
        </section>

        <!-- Section 5 -->
        <section class="terms-section">
          <h2 class="section-title">5. Conditions Spécifiques au Billet</h2>

          <h3 class="subsection-title">5.1. Billet Électronique</h3>
          <p class="section-text">
            Le billet est émis sous forme électronique. L'Utilisateur doit être en mesure de présenter le billet (imprimé ou sur support mobile) et une pièce d'identité valide au moment de l'embarquement.
          </p>

          <h3 class="subsection-title">5.2. Modification et Annulation</h3>
          <p class="section-text">
            Les conditions de modification, d'échange ou d'annulation du billet sont celles fixées par le Transporteur concerné. UrbanGO applique strictement ces conditions et ne peut y déroger.
          </p>
          <p class="section-text">
            L'Utilisateur est invité à consulter les conditions spécifiques du Transporteur avant de finaliser son achat.
          </p>

          <h3 class="subsection-title">5.3. Absence de Droit de Rétractation</h3>
          <p class="section-text">
            Conformément aux dispositions relatives à la vente de services de transport de passagers, l'achat de billet sur la Plateforme ne bénéficie pas du droit de rétractation prévu par la loi sur la consommation.
          </p>
        </section>

        <!-- Section 6 -->
        <section class="terms-section">
          <h2 class="section-title">6. Responsabilités</h2>

          <h3 class="subsection-title">6.1. Responsabilité de UrbanGO</h3>
          <p class="section-text">
            La responsabilité d'UrbanGO est limitée à la qualité de son service d'intermédiation et de vente en ligne. UrbanGO ne garantit pas la disponibilité constante des services de transport proposés par les Transporteurs.
          </p>

          <h3 class="subsection-title">6.2. Responsabilité du Transporteur</h3>
          <p class="section-text">
            Le Transporteur est seul responsable de l'exécution du voyage, y compris, mais sans s'y limiter :
          </p>
          <ul class="section-list">
            <li>Le respect des horaires et des itinéraires.</li>
            <li>La sécurité des Passagers et des bagages.</li>
            <li>La qualité et la conformité du véhicule.</li>
          </ul>
          <p class="section-text">
            Toute réclamation relative à l'exécution du transport doit être adressée directement au Transporteur.
          </p>
        </section>

        <!-- Section 7 -->
        <section class="terms-section">
          <h2 class="section-title">7. Propriété Intellectuelle</h2>
          <p class="section-text">
            Tous les éléments de la Plateforme (textes, images, logos, logiciels, etc.) sont la propriété exclusive d'UrbanGO et sont protégés par les lois ivoiriennes et internationales relatives à la propriété intellectuelle.
          </p>
        </section>

        <!-- Section 8 -->
        <section class="terms-section">
          <h2 class="section-title">8. Droit Applicable et Règlement des Litiges</h2>

          <h3 class="subsection-title">8.1. Droit Applicable</h3>
          <p class="section-text">
            Les présentes CGU sont régies par le droit ivoirien.
          </p>

          <h3 class="subsection-title">8.2. Règlement des Litiges</h3>
          <p class="section-text">
            En cas de litige, l'Utilisateur s'engage à contacter UrbanGO en premier lieu pour tenter de trouver une solution amiable.
          </p>
          <p class="section-text">
            À défaut de résolution amiable, les tribunaux compétents d'Abidjan, Côte d'Ivoire, seront seuls compétents pour connaître du litige.
          </p>
        </section>

        <!-- Section 9 -->
        <section class="terms-section">
          <h2 class="section-title">9. Contact</h2>
          <p class="section-text">
            Pour toute question relative aux présentes CGU, l'Utilisateur peut contacter UrbanGO :
          </p>
          <div class="contact-box">
            <p class="contact-box__text">
              <strong>UrbanGO</strong><br>
              À l'attention de Mohamed FADIGA, Gérant<br>
              Yopougon quartier Maroc, station Pétro Ivoire
            </p>
            <p class="contact-box__text">
              E-mail : <strong>[Adresse e-mail de contact]</strong><br>
              Téléphone : <strong>[Numéro de téléphone]</strong>
            </p>
          </div>
        </section>

        <!-- References -->
        <div class="references-box">
          <h3 class="references-title">Références</h3>
          <p class="references-text">
            [1] Law No. 2013-546 of 30 July 2013 on electronic transactions (accessed via droit-afrique.com).
          </p>
          <p class="references-text">
            [2] Law No. 2016-412 of 15 June 2016 on consumption (accessed via oqsf.ci).
          </p>
        </div>
      </div>
    </div>
  </v-app>
</template>

<script>
export default {
  name: 'TermsAndConditionContent',
  methods: {
    goBack() {
      this.$router.go(-1);
    },
  },
  computed: {
    headerStyle() {
      return {
        position: 'fixed',
        top: this.$vuetify.application.top + 'px',
        left: this.$vuetify.application.left + 'px',
        right: this.$vuetify.application.right + 'px',
        zIndex: 5,
      };
    },
    contentStyle() {
      return {
        paddingTop: (this.$vuetify.application.top + 100) + 'px',
      };
    },
  },
};
</script>

<style scoped>
/* Global */
#terms-conditions {
  background: linear-gradient(135deg, #f8fafc 0%, #e8f5f1 100%);
  min-height: 100vh;
}

/* Header */
.terms-header {
  background: linear-gradient(to right, #159e72 0%, #1e3a8a 100%);
  padding: 1rem 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.terms-header__content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.terms-header__back {
  background: rgba(255, 255, 255, 0.1) !important;
  transition: all 0.2s ease;
}

.terms-header__back:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  transform: translateX(-2px);
}

.terms-header__title {
  font-family: "Montserrat";
  font-weight: 700;
  font-size: 18px;
  color: white;
  margin: 0;
  line-height: 1.2;
}

.terms-header__date {
  font-family: "Montserrat";
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  margin: 2px 0 0 0;
  font-style: italic;
}

/* Content */
.terms-content {
  padding: 40px 24px;
  max-width: 1200px;
}

.terms-card {
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
}

/* Sections */
.terms-section {
  margin-bottom: 40px;
}

.section-title {
  font-family: "Montserrat";
  font-weight: 700;
  font-size: 20px;
  color: #0f172a;
  margin-bottom: 16px;
}

.subsection-title {
  font-family: "Montserrat";
  font-weight: 600;
  font-size: 17px;
  color: #334155;
  margin: 24px 0 16px 0;
}

.section-text {
  font-family: "Montserrat";
  font-size: 14px;
  color: #475569;
  line-height: 1.7;
  margin-bottom: 12px;
  text-align: justify;
}

.section-list {
  font-family: "Montserrat";
  font-size: 14px;
  color: #475569;
  line-height: 1.7;
  margin: 16px 0;
  padding-left: 24px;
}

.section-list li {
  margin-bottom: 8px;
}

/* Tables */
.terms-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin: 20px 0;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #e2e8f0;
}

.table-row:last-child {
  border-bottom: none;
}

.table-header {
  background: #dbeafe;
}

.table-header .table-cell {
  font-weight: 700;
  color: #0f172a;
}

.table-cell {
  font-family: "Montserrat";
  font-size: 13px;
  color: #475569;
  padding: 14px;
  line-height: 1.5;
  flex: 1;
}

.table-cell:first-child {
  flex: 0 0 35%;
}

.cell-bold {
  font-weight: 600;
  color: #0f172a;
}

/* Contact Box */
.contact-box {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 20px;
  margin-top: 16px;
}

.contact-box__text {
  font-family: "Montserrat";
  font-size: 13px;
  color: #334155;
  line-height: 1.6;
  margin-bottom: 12px;
}

.contact-box__text:last-child {
  margin-bottom: 0;
}

/* References Box */
.references-box {
  background: #f8fafc;
  border-radius: 8px;
  padding: 20px;
  margin-top: 32px;
}

.references-title {
  font-family: "Montserrat";
  font-weight: 700;
  font-size: 16px;
  color: #0f172a;
  margin-bottom: 12px;
}

.references-text {
  font-family: "Montserrat";
  font-size: 12px;
  color: #64748b;
  line-height: 1.6;
  margin: 0 0 8px 0;
}

.references-text:last-child {
  margin-bottom: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .terms-header {
    padding: 0.75rem 1rem;
  }

  .terms-header__title {
    font-size: 16px;
  }

  .terms-header__date {
    font-size: 11px;
  }

  .terms-card {
    padding: 24px;
  }

  .section-title {
    font-size: 18px;
  }

  .subsection-title {
    font-size: 16px;
  }

  .table-row {
    flex-direction: column;
  }

  .table-cell {
    flex: 1 !important;
  }

  .table-cell:first-child {
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
  }
}
</style>
